import React, { FC, useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
} from "@mui/material";
import {
  LastSchriftDetailsDto,
  CreditTransferExportOptions,
  AccountSEPAData,
} from "../types/sepa-export.types";

interface CreditTransferExportDialogProps {
  open: boolean;
  onClose: () => void;
  selectedTransactions: LastSchriftDetailsDto[];
  accountSepaData?: AccountSEPAData[];
  onExport: (exportOptions: Omit<CreditTransferExportOptions, "creationDateTime">) => Promise<void>;
  loading?: boolean;
}

const CreditTransferExportDialog: FC<CreditTransferExportDialogProps> = ({
  open,
  onClose,
  selectedTransactions,
  accountSepaData = [],
  onExport,
  loading = false,
}) => {
  const [exportOptions, setExportOptions] = useState<CreditTransferExportOptions>({
    messageId: `SEPA-CT-${Date.now()}`,
    debtorName: "",
    debtorIBAN: "",
    debtorBIC: "",
    debtorCreditorId: "",
    creationDateTime: new Date(),
  });

  const [selectedAccountId, setSelectedAccountId] = useState<string>("");
  const [errors, setErrors] = useState<Partial<CreditTransferExportOptions>>({});

  // Auto-select account and pre-fill SEPA data if only one account
  useEffect(() => {
    if (accountSepaData.length === 1) {
      const accountData = accountSepaData[0];
      setSelectedAccountId(accountData.accountId);
      prefillSEPAData(accountData);
    } else if (accountSepaData.length > 1) {
      // Reset fields if multiple accounts
      setSelectedAccountId("");
      setExportOptions((prev) => ({
        ...prev,
        debtorName: "",
        debtorIBAN: "",
        debtorBIC: "",
        debtorCreditorId: "",
      }));
    }
  }, [accountSepaData]);

  const prefillSEPAData = (accountData: AccountSEPAData) => {
    // IMPORTANT MAPPING: accountSepaData fields are named "creditor" from debit workflow
    // But for credit transfers, clinic = debtor (sender), so we map:
    // accountSepaData.creditorName → exportOptions.debtorName
    setExportOptions((prev) => ({
      ...prev,
      debtorName:
        accountData.creditorName ||
        accountData.accountFullName ||
        accountData.accountName ||
        "",
      debtorIBAN: accountData.creditorIBAN || "",
      debtorBIC: accountData.creditorBIC || "",
      debtorCreditorId: accountData.creditorId || "",
    }));
  };

  const handleAccountChange = (event: SelectChangeEvent<string>) => {
    const accountId = event.target.value;
    setSelectedAccountId(accountId);

    const accountData = accountSepaData.find(
      (acc) => acc.accountId === accountId,
    );
    if (accountData) {
      prefillSEPAData(accountData);
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<CreditTransferExportOptions> = {};

    if (!exportOptions.debtorName.trim()) {
      newErrors.debtorName = "Schuldner Name ist erforderlich";
    }

    if (!exportOptions.debtorIBAN.trim()) {
      newErrors.debtorIBAN = "IBAN ist erforderlich";
    } else if (
      !/^[A-Z]{2}[0-9]{2}[A-Z0-9]{4}[0-9]{7}([A-Z0-9]?){0,16}$/.test(
        exportOptions.debtorIBAN.replace(/\s/g, ""),
      )
    ) {
      newErrors.debtorIBAN = "Ungültiges IBAN-Format";
    }

    if (!exportOptions.debtorBIC.trim()) {
      newErrors.debtorBIC = "BIC ist erforderlich";
    } else if (
      !/^[A-Z]{6}[A-Z2-9][A-NP-Z0-9]([A-Z0-9]{3})?$/.test(
        exportOptions.debtorBIC,
      )
    ) {
      newErrors.debtorBIC = "Ungültiges BIC-Format";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleExport = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      // Remove creationDateTime before sending to API
      const { creationDateTime, ...exportOptionsWithoutDate } = exportOptions;
      await onExport(exportOptionsWithoutDate);
      onClose();
    } catch (error) {
      console.error("Credit Transfer export failed:", error);
    }
  };

  const totalAmount = selectedTransactions.reduce((sum, transaction) => {
    // Use absolute value for refunds (they're stored as negative)
    return sum + Math.abs(Number(transaction.amount));
  }, 0);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>SEPA Rückerstattung Export (Credit Transfer)</DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 3 }}>
          <Alert severity="info" sx={{ mb: 2 }}>
            {selectedTransactions.length} Rückerstattungs-Transaktionen ausgewählt
            <br />
            Gesamtbetrag: {totalAmount.toFixed(2).replace(".", ",")} €
          </Alert>
          <Alert severity="warning" sx={{ mb: 1 }}>
            <strong>Credit Transfer (pain.001.001.03):</strong> Der Mandant ist der <strong>Schuldner</strong> (Sender) und überweist Geld an Patienten (Gläubiger).
          </Alert>
        </Box>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {/* Warning if no account data available */}
          {accountSepaData.length === 0 && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              <strong>Warnung:</strong> Keine Mandantendaten für automatische
              Befüllung verfügbar.
              <br />
              Bitte füllen Sie die SEPA-Parameter manuell aus.
            </Alert>
          )}

          {/* Account Selection - only show if multiple accounts */}
          {accountSepaData.length > 1 && (
            <FormControl fullWidth>
              <InputLabel id="account-select-label">
                Mandant auswählen
              </InputLabel>
              <Select
                labelId="account-select-label"
                value={selectedAccountId}
                onChange={handleAccountChange}
                label="Mandant auswählen"
                required
              >
                {accountSepaData.map((account) => (
                  <MenuItem key={account.accountId} value={account.accountId}>
                    {account.accountFullName || account.accountName} (
                    {account.transactionCount} Transaktionen)
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {/* Account Info Display - show selected account info */}
          {accountSepaData.length === 1 && (
            <Alert severity="info" sx={{ mb: 1 }}>
              <strong>Mandant:</strong>{" "}
              {accountSepaData[0].accountFullName ||
                accountSepaData[0].accountName}
              {/* <br />
              <strong>Transaktionen:</strong>{" "}
              {accountSepaData[0].transactionCount} */}
            </Alert>
          )}

          <TextField
            label="Nachrichten-ID"
            value={exportOptions.messageId}
            onChange={(e) =>
              setExportOptions({ ...exportOptions, messageId: e.target.value })
            }
            fullWidth
            required
            disabled
            helperText="Automatisch generiert"
          />

          <TextField
            label="Schuldner Name"
            value={exportOptions.debtorName}
            onChange={(e) =>
              setExportOptions({
                ...exportOptions,
                debtorName: e.target.value,
              })
            }
            fullWidth
            required
            error={!!errors.debtorName}
            helperText={
              errors.debtorName || "Automatisch befüllt aus Mandantendaten"
            }
            placeholder="z.B. Physiotherapie Muster GmbH"
          />

          <TextField
            label="Schuldner IBAN"
            value={exportOptions.debtorIBAN}
            onChange={(e) =>
              setExportOptions({
                ...exportOptions,
                debtorIBAN: e.target.value.replace(/\s/g, "").toUpperCase(),
              })
            }
            fullWidth
            required
            error={!!errors.debtorIBAN}
            helperText={
              errors.debtorIBAN || "Automatisch befüllt aus Mandantendaten"
            }
            placeholder="DE89370400440532013000"
          />

          <TextField
            label="Schuldner BIC"
            value={exportOptions.debtorBIC}
            onChange={(e) =>
              setExportOptions({
                ...exportOptions,
                debtorBIC: e.target.value.toUpperCase(),
              })
            }
            fullWidth
            required
            error={!!errors.debtorBIC}
            helperText={
              errors.debtorBIC ||
              "Erforderlich für Credit Transfer"
            }
            placeholder="COBADEFFXXX"
          />

          <TextField
            label="Gläubiger-ID (Optional)"
            value={exportOptions.debtorCreditorId}
            onChange={(e) =>
              setExportOptions({ ...exportOptions, debtorCreditorId: e.target.value })
            }
            fullWidth
            helperText="Optional; nur falls vorhanden"
            placeholder="DE98ZZZ09999999999"
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Abbrechen
        </Button>
        <Button
          onClick={handleExport}
          variant="contained"
          disabled={loading || selectedTransactions.length === 0}
          startIcon={loading ? <CircularProgress size={20} /> : null}
        >
          {loading ? "Exportiere..." : "SEPA Credit Transfer Exportieren"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreditTransferExportDialog;
